import { X } from "lucide-react";
import { useTranslation } from "react-i18next";

import { useFocusTrap } from "../lib/use-focus-trap";
import { cn } from "../lib/utils";

/**
 * Default Terms & Conditions modal - shown when no operator-supplied
 * ``brand_config.terms_url`` is set. Content is the V1 portal's
 * baseline T&Cs (DCPOS-Portal/src/layouts/DefaultLayout.vue), kept
 * verbatim so existing operators see no behavior change while admins
 * gradually populate per-partner overrides through the BrandEditor.
 *
 * The modal is generic - it just renders an i18n-keyed body. en /
 * es / fr-CA each carry the eight-section structure under
 * ``footer.termsBody.*`` so the cashier reads it in their selected
 * language.
 *
 * Print-friendly: ``print:p-0 print:max-w-none`` so a cashier can
 * print the document if they need a paper copy on file.
 */
export function TermsModal({
  open,
  onClose,
  partnerName,
  className,
}: {
  readonly open: boolean;
  readonly onClose: () => void;
  /** Resolved closest-ancestor partner name (cascade walk up to the
   *  platform). Interpolated into the intro + liability paragraphs so
   *  the static T&C reads as the operator's actual partner instead of
   *  always saying "Finterra". (#159 follow-up) */
  readonly partnerName: string;
  readonly className?: string;
}) {
  const { t } = useTranslation();
  const dialogRef = useFocusTrap<HTMLDivElement>(open, onClose);

  if (!open) return null;

  // Eight numbered sections matching the V1 portal verbatim. Each is
  // an array of (title, paragraph) tuples so the i18n layer stays
  // flat - no nested objects per section.
  const sections: ReadonlyArray<{ titleKey: string; bodyKey: string }> = [
    { titleKey: "footer.termsBody.useTitle", bodyKey: "footer.termsBody.useBody" },
    { titleKey: "footer.termsBody.accountsTitle", bodyKey: "footer.termsBody.accountsBody" },
    { titleKey: "footer.termsBody.paymentsTitle", bodyKey: "footer.termsBody.paymentsBody" },
    { titleKey: "footer.termsBody.privacyTitle", bodyKey: "footer.termsBody.privacyBody" },
    { titleKey: "footer.termsBody.disclaimerTitle", bodyKey: "footer.termsBody.disclaimerBody" },
    { titleKey: "footer.termsBody.liabilityTitle", bodyKey: "footer.termsBody.liabilityBody" },
    { titleKey: "footer.termsBody.changesTitle", bodyKey: "footer.termsBody.changesBody" },
    { titleKey: "footer.termsBody.contactTitle", bodyKey: "footer.termsBody.contactBody" },
  ];

  return (
    <div
      ref={dialogRef}
      className={cn("fixed inset-0 z-50 flex items-center justify-center p-4", className)}
      role="dialog"
      aria-modal="true"
      aria-labelledby="terms-title"
    >
      <div aria-hidden="true" onClick={onClose} className="absolute inset-0 bg-black/40" />
      <div className="relative flex max-h-[90vh] w-full max-w-2xl flex-col rounded-lg bg-surface shadow-xl print:max-w-none print:rounded-none print:shadow-none">
        <header className="flex items-start justify-between border-b border-line px-5 py-3 print:border-0">
          <h2 id="terms-title" className="text-base font-semibold text-strong">
            {t("footer.terms")}
          </h2>
          <button
            type="button"
            onClick={onClose}
            aria-label={t("common.cancel")}
            className="rounded p-1.5 text-faint hover:bg-surface-strong hover:text-foreground print:hidden"
          >
            <X className="h-4 w-4" />
          </button>
        </header>

        <div className="flex-1 overflow-y-auto px-5 py-4 text-sm leading-relaxed text-foreground print:overflow-visible print:px-0 print:py-0">
          <p className="mb-3">{t("footer.termsBody.intro", { partnerName })}</p>
          {sections.map((s, i) => (
            <div key={s.titleKey} className="mb-3">
              <h3 className="mb-1 text-sm font-semibold text-strong">
                {i + 1}. {t(s.titleKey)}
              </h3>
              <p>{t(s.bodyKey, { partnerName })}</p>
            </div>
          ))}
        </div>

        <footer className="flex justify-end border-t border-line px-5 py-3 print:hidden">
          <button
            type="button"
            onClick={onClose}
            className="rounded-md bg-foreground px-4 py-1.5 text-sm font-medium text-canvas hover:opacity-90"
          >
            {t("footer.termsClose")}
          </button>
        </footer>
      </div>
    </div>
  );
}
